/* eslint-disable @typescript-eslint/no-explicit-any */
import { Input, Table, Typography } from 'antd'
import { regexNumber } from 'constants/regex'
import { FormikErrors } from 'formik'
import {
  CampaignsProgramsCreateParticipantsInner,
  CampaignsProgramsCreateTargetsInner,
} from 'generated/api'
import { thousandSeparator, unThousandSeparator } from 'helpers/numbers'
import { validationInputZero } from 'helpers/validation'
import { ExtendedCampaignsProgram, ExtendedUser } from '../hooks/useCampaignCreation'
import { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'

interface IProps {
  search: string
  countDuration: (startTime: number, endTime: number) => string[]
  generateObjects: (months: string[]) => any
  data: ExtendedUser[]
  detailCampaign: ExtendedCampaignsProgram
  handleParticipants: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined,
  ) => Promise<void> | Promise<FormikErrors<ExtendedCampaignsProgram>>
}

export default function TableParticipant(props: Readonly<IProps>) {
  const { data, detailCampaign, handleParticipants, countDuration, generateObjects, search } = props

  const getListIdByName = () => {
    const participantFound = data.filter(
      item => item.name && item.name.toLowerCase().includes(search.toLowerCase()),
    )
    const filteredUser = data.filter(itemB => participantFound.some(itemA => itemA.id === itemB.id))
    return search == '' ? data : filteredUser
  }

  const getNameById = (id: string) => {
    const participantFound = data.find(item => item.id === id)
    return participantFound ? participantFound.name : '-'
  }

  const checkDisabled = (userId: string) => {
    const temp = detailCampaign.participantsExtend.find(
      participant => participant.userId === userId,
    )
    return temp ? false : true
  }

  const getValue = (
    userId: string,
    type: 'DAILY' | 'MONTHLY' | 'QUARTERLY',
    key: keyof CampaignsProgramsCreateTargetsInner = 'targetFfbWeightTonne',
    month?: string,
  ): number => {
    const participant = detailCampaign.participantsExtend.find(
      participant => participant.userId === userId,
    )
    if (!participant) {
      return 0
    }
    const monthIndex = dayjs(month, 'MMMM').month()
    const target =
      type == 'QUARTERLY'
        ? participant.targets.find(target => target.type === type)
        : participant.targets.find(
            target => target.type === type && dayjs(target.startTime).month() === monthIndex,
          )

    if (!target) {
      return 0
    }
    return target[key] as number
  }

  const handleChangeList = (
    userId: string,
    start: number,
    type: 'DAILY' | 'MONTHLY' | 'QUARTERLY',
    newWeight: number,
  ) => {
    const temp = detailCampaign.participantsExtend
    const updatedParticipants = temp?.map((participant: any) => {
      if (participant.userId === userId) {
        const updatedTargets = participant?.targets?.map((target: any) => {
          if (target.type === type) {
            if (type == 'QUARTERLY') {
              return {
                ...target,
                targetFfbWeightTonne: newWeight,
              }
            } else {
              if (target.startTime === start) {
                return {
                  ...target,
                  targetFfbWeightTonne: newWeight,
                }
              }
            }
          }
          return target
        })
        return {
          ...participant,
          targets: updatedTargets,
        }
      }
      return participant
    })

    handleParticipants('participantsExtend', updatedParticipants)
  }

  const getColumnsMonth = (): ColumnsType<CampaignsProgramsCreateParticipantsInner> => {
    const monthsArray = countDuration(detailCampaign.startTime, detailCampaign.endTime)
    return monthsArray?.map(item => ({
      title: `Target ${item}`,
      children: [
        {
          title: 'Harian (Ton)',
          dataIndex: 'daily',
          width: '150px',
          key: `daily_tonne_${item}`,
          render: (_, record) => (
            <Input
              data-testid="program-name"
              disabled={checkDisabled(record.userId)}
              className="px-4 py-2 rounded-lg"
              value={
                getValue(record.userId, 'DAILY', 'targetFfbWeightTonne', item) == 0
                  ? ''
                  : thousandSeparator(
                      getValue(record.userId, 'DAILY', 'targetFfbWeightTonne', item),
                    )
              }
              maxLength={7}
              onChange={event => {
                const val = unThousandSeparator(event.target.value) ?? ''
                if (regexNumber.test(validationInputZero(val))) {
                  handleChangeList(
                    record.userId,
                    getValue(record.userId, 'DAILY', 'startTime', item),
                    'DAILY',
                    Number(val),
                  )
                }
              }}
            />
          ),
        },
        {
          title: 'Bulanan (Ton)',
          dataIndex: 'monthly',
          key: `monthly_tonne_${item}`,
          width: '150px',
          render: (_, record) => (
            <Input
              data-testid="program-name"
              disabled={checkDisabled(record.userId)}
              maxLength={7}
              className="px-4 py-2 rounded-lg"
              value={
                getValue(record.userId, 'MONTHLY', 'targetFfbWeightTonne', item) == 0
                  ? ''
                  : thousandSeparator(
                      getValue(record.userId, 'MONTHLY', 'targetFfbWeightTonne', item),
                    )
              }
              onChange={event => {
                const val = unThousandSeparator(event.target.value) ?? ''
                if (regexNumber.test(validationInputZero(val))) {
                  handleChangeList(
                    record.userId,
                    getValue(record.userId, 'MONTHLY', 'startTime', item),
                    'MONTHLY',
                    Number(val),
                  )
                }
              }}
            />
          ),
        },
      ],
    }))
  }

  const columnsMonth: any = getColumnsMonth() ?? []

  const columns: ColumnsType<ExtendedUser> = [
    {
      title: 'Nama',
      dataIndex: 'name',
      fixed: true,
      width: '250px',
      render: (_, record) => (
        <div data-testid="cell_name" className="flex flex-col">
          <Typography.Text className="font-medium text-gray-800">
            {getNameById(record.userId)}
          </Typography.Text>
        </div>
      ),
    },
    ...columnsMonth,
    {
      title: 'Target Triwulan (Ton)',
      dataIndex: 'phoneNo',
      fixed: 'right',
      width: '200px',
      render: (_, record) => (
        <Input
          data-testid="program-name"
          disabled={checkDisabled(record.userId)}
          className="px-4 py-2 rounded-lg"
          value={
            getValue(record.userId, 'QUARTERLY', 'targetFfbWeightTonne') == 0
              ? ''
              : thousandSeparator(getValue(record.userId, 'QUARTERLY', 'targetFfbWeightTonne'))
          }
          maxLength={6}
          onChange={event => {
            const val = unThousandSeparator(event.target.value) ?? ''
            if (regexNumber.test(validationInputZero(val))) {
              handleChangeList(
                record.userId,
                getValue(record.userId, 'QUARTERLY', 'startTime'),
                'QUARTERLY',
                Number(val),
              )
            }
          }}
        />
      ),
    },
  ]

  const rowSelection = {
    onSelect: (record: ExtendedUser) => {
      const prevArray = detailCampaign.participantsExtend
      const formattedRecord = {
        id: record.id,
        userId: record.id,
        targets: generateObjects(countDuration(detailCampaign.startTime, detailCampaign.endTime)),
      } as ExtendedUser
      const newArray = prevArray.some(item => item.id === formattedRecord.id)
        ? prevArray.filter(item => item.id !== formattedRecord.id)
        : [...prevArray, formattedRecord]

      handleParticipants('participantsExtend', newArray)
    },
    hideSelectAllCheckbox: true,
  }

  const selectedUserss = detailCampaign?.participantsExtend?.map(res => res.id)

  return (
    <div style={{ overflowY: 'auto', maxHeight: '500px' }}>
      <Table
        rowKey="id"
        loading={false}
        rowSelection={{
          type: 'checkbox',
          ...rowSelection,
          fixed: true,
          selectedRowKeys: selectedUserss,
        }}
        className="table-participant"
        data-testid="table-participant"
        pagination={false}
        columns={columns}
        dataSource={getListIdByName()}
        scroll={{
          x: 1000,
        }}
      />
    </div>
  )
}
